const HEX = /^#([0-9A-Fa-f]{6})$/;

/** Keep authored email colors and copy when a client is in dark mode. */
const LIGHT_COLOR_SCHEME_STYLE = "color-scheme:only light;";
const LIGHT_COLOR_SCHEME_HEAD = `<meta name="color-scheme" content="light only" />
    <meta name="supported-color-schemes" content="light only" />
    <style>:root { color-scheme: only light; }</style>`;

/** Matches quest-server EMAIL_BRAND.logo for invite preview parity. */
const QUEST_LOGO_URL =
  'https://www.quest-q.com/static/media/Logo.7e536f3ca00fdcfe9bd6.png';

export const EMAIL_TEMPLATE_LIMITS = Object.freeze({
  subject: 80,
  headline: 40,
  ctaSubject: 60,
  cta: 30,
});

export const PAGE_FONTS = Object.freeze([
  {
    id: 'Montserrat',
    label: 'Montserrat (Default)',
    stack: "'Montserrat','Helvetica Neue',Helvetica,Arial,sans-serif",
    google: 'Montserrat:wght@400;500;600;700',
  },
  {
    id: 'Arial',
    label: 'Arial',
    stack: 'Arial,Helvetica,sans-serif',
  },
  {
    id: 'Georgia',
    label: 'Georgia',
    stack: 'Georgia,Times,serif',
  },
]);

export const EVENT_NAME_FONTS = Object.freeze([
  {
    id: 'Corinthia',
    label: 'Corinthia (Default)',
    stack: "'Corinthia','Great Vibes','Palatino Linotype',cursive",
    google: 'Corinthia:wght@400;700',
  },
  {
    id: 'Great Vibes',
    label: 'Great Vibes',
    stack: "'Great Vibes',cursive",
    google: 'Great+Vibes',
  },
  {
    id: 'Georgia',
    label: 'Georgia',
    stack: 'Georgia,Times,serif',
  },
]);

export const DESIGN_COLOR_FIELDS = Object.freeze([
  {
    group: 'COMPONENT COLORS',
    items: [
      { key: 'pageBackground', label: 'Page Background' },
      { key: 'eventDetailsBackground', label: 'Event Details Background' },
      { key: 'ctaBackground', label: 'CTA Background' },
      { key: 'ctaText', label: 'CTA Text' },
      { key: 'bookLogoBorder', label: 'Book Logo Border' },
    ],
  },
  {
    group: 'TEXT COLORS',
    items: [
      { key: 'congratulationsText', label: 'Congratulations Text' },
      { key: 'headlineText', label: 'Headline Text' },
      { key: 'eventNameText', label: 'Event Name Text' },
      { key: 'eventDescriptionText', label: 'Event Description Text' },
      { key: 'detailTitleText', label: 'Detail Title Text' },
      { key: 'detailValueText', label: 'Detail Value Text' },
      { key: 'attributionLabelText', label: 'Attribution Label Text' },
      { key: 'attributionValueText', label: 'Attribution Value Text' },
    ],
  },
]);

export const DEFAULT_DESIGN_COLORS = Object.freeze({
  pageBackground: '#1A1A1A',
  eventDetailsBackground: '#444444',
  ctaBackground: '#3361CF',
  ctaText: '#FFFFFF',
  bookLogoBorder: '#B9A3E1',
  congratulationsText: '#FFFFFF',
  headlineText: '#FFFFFF',
  eventNameText: '#FFD70F',
  eventDescriptionText: '#FFFFFF',
  detailTitleText: '#C3C3C3',
  detailValueText: '#FFFFFF',
  attributionLabelText: '#FFFFFF',
  attributionValueText: '#FFFFFF',
});

const clamp = (value, max) => String(value || '').slice(0, max);

const normalizeHex = (value, fallback) => {
  const next = String(value || '').trim();
  return HEX.test(next) ? next.toUpperCase() : fallback;
};

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Outlook ignores text-transform, so uppercase the copy and leave {{tokens}} intact. */
const uppercaseCopy = (value) =>
  String(value || '').replace(/\{\{[^}]+\}\}|[^{]+/g, (part) =>
    part.startsWith('{{') ? part : part.toUpperCase()
  );

/** Characters that can close url('...') or the surrounding style attribute. */
const UNSAFE_CSS_URL = /[\s"'()\\<>]/;

/** https? URL safe to place inside url('...'). Empty when the value must be skipped. */
const safeCssBackgroundImageUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw || UNSAFE_CSS_URL.test(raw)) return '';
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
    if (!parsed.hostname.includes('.')) return '';
    if (UNSAFE_CSS_URL.test(parsed.href)) return '';
    return parsed.href;
  } catch {
    return '';
  }
};

const fontById = (list, id) =>
  list.find((item) => item.id === id) || list[0];

export const defaultInviteDesign = (_bookName = 'Promotion') => ({
  subject: clamp(
    "Invitation: Promotion Event For {{bookName}}",
    EMAIL_TEMPLATE_LIMITS.subject
  ),
  headline: 'You Are Invited To',
  ctaSubject: 'Please Confirm Your Attendance',
  cta: 'Mark Your Attendance',
  bookLogoUrl: '',
  companyLogoUrl: '',
  backgroundImageUrl: '',
  pageFont: 'Montserrat',
  eventNameFont: 'Corinthia',
  colors: { ...DEFAULT_DESIGN_COLORS },
});

export const mergeInviteDesign = (input = {}, bookName = 'Promotion') => {
  const base = defaultInviteDesign(bookName);
  const colors = { ...base.colors };
  const incomingColors = input.colors && typeof input.colors === 'object' ? input.colors : {};
  for (const key of Object.keys(DEFAULT_DESIGN_COLORS)) {
    colors[key] = normalizeHex(incomingColors[key], DEFAULT_DESIGN_COLORS[key]);
  }
  return {
    subject: clamp(
      input.subject != null ? input.subject : base.subject,
      EMAIL_TEMPLATE_LIMITS.subject
    ),
    headline: clamp(
      input.headline != null ? input.headline : base.headline,
      EMAIL_TEMPLATE_LIMITS.headline
    ),
    ctaSubject: clamp(
      input.ctaSubject != null ? input.ctaSubject : base.ctaSubject,
      EMAIL_TEMPLATE_LIMITS.ctaSubject
    ),
    cta: clamp(input.cta != null ? input.cta : base.cta, EMAIL_TEMPLATE_LIMITS.cta),
    bookLogoUrl: String(input.bookLogoUrl || '').trim(),
    companyLogoUrl: String(input.companyLogoUrl || '').trim(),
    backgroundImageUrl: safeCssBackgroundImageUrl(input.backgroundImageUrl),
    pageFont: fontById(PAGE_FONTS, input.pageFont).id,
    eventNameFont: fontById(EVENT_NAME_FONTS, input.eventNameFont).id,
    colors,
  };
};

export const EDITOR_PREVIEW_DATA = Object.freeze({
  employeeName: 'Participant Name',
  firstName: 'Participant Name',
  bookName: 'Book Name',
  eventName: 'Event Name',
  eventId: 'EI-000000',
  eventDate: 'Date Month, Year',
  eventTime: 'Time AM/PM',
  venue:
    'Lorem ipsum dolor sit amet consectetur scelerisque.',
  description:
    'Lorem ipsum dolor sit amet consectetur. Feugiat libero ornare mauris sagittis. Est nunc mauris arcu quis quam lectus purus at sem cursus pellentesque.',
  coordinatorName: 'Host Name',
  takeActionUrl: '#',
  locationUrl: '#',
});

export const compileInviteEmail = (rawDesign = {}, bookName = 'Promotion') => {
  const design = mergeInviteDesign(rawDesign, bookName);
  const c = design.colors;
  const pageFont = fontById(PAGE_FONTS, design.pageFont).stack;
  const eventFont = fontById(EVENT_NAME_FONTS, design.eventNameFont).stack;
  const headline = escapeHtml(uppercaseCopy(design.headline));
  const cta = escapeHtml(design.cta);
  const ctaSubject = escapeHtml(uppercaseCopy(design.ctaSubject));
  const backgroundImage = design.backgroundImageUrl
    ? escapeHtml(design.backgroundImageUrl)
    : '';
  const heroBg = [
    `background-color:${c.pageBackground}`,
    backgroundImage
      ? `background-image:url('${backgroundImage}');background-repeat:no-repeat;background-position:center center;background-size:cover`
      : '',
  ]
    .filter(Boolean)
    .join(';')
    .concat(';');
  const heroBackgroundAttr = backgroundImage
    ? ` background="${backgroundImage}"`
    : '';
  const heroVmlOpen = backgroundImage
    ? `<!--[if gte mso 9]><v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:420px;"><v:fill type="frame" src="${backgroundImage}" color="${c.pageBackground}" /><v:textbox inset="0,0,0,0" style="mso-fit-shape-to-text:true"><![endif]-->`
    : '';
  const heroVmlClose = backgroundImage
    ? `<!--[if gte mso 9]></v:textbox></v:rect><![endif]-->`
    : '';
  const bookLogo = design.bookLogoUrl
    ? `<img src="${escapeHtml(design.bookLogoUrl)}" alt="Book logo" width="72" height="72" style="display:block;width:72px;height:72px;border-radius:50%;border:0;outline:none;text-decoration:none;" />`
    : `<table role="presentation" cellspacing="0" cellpadding="0" border="0" bgcolor="#d9d9d9" style="background-color:#d9d9d9;"><tr><td align="center" valign="middle" width="72" height="72" bgcolor="#d9d9d9" style="width:72px;height:72px;background-color:#d9d9d9;color:#555555;font-size:8px;letter-spacing:0.04em;text-align:center;font-family:Arial,Helvetica,sans-serif;">BOOK LOGO</td></tr></table>`;
  const companyLogo = design.companyLogoUrl
    ? `<img src="${escapeHtml(design.companyLogoUrl)}" alt="Company logo" width="140" style="display:block;margin:0 auto;max-width:140px;height:auto;border:0;outline:none;text-decoration:none;" />`
    : `<table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" bgcolor="#111111" style="background-color:#111111;"><tr><td align="center" bgcolor="#111111" style="background-color:#111111;color:#ffffff;font-size:11px;letter-spacing:0.08em;font-family:Arial,Helvetica,sans-serif;padding:8px 18px;">COMPANY LOGO</td></tr></table>`;

  const canvasBg = '#fafafa';
  const html = `<!doctype html>
<html lang="en" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" style="background-color:${canvasBg};${LIGHT_COLOR_SCHEME_STYLE}">
  <head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    ${LIGHT_COLOR_SCHEME_HEAD}
    <meta name="format-detection" content="address=no" />
    <style>
      .quest-venue,
      .quest-venue a {
        color: ${c.detailValueText} !important;
        text-decoration: none !important;
        pointer-events: none !important;
      }
    </style>
    <!--[if mso]>
      <style>td, p, a, span, strong { font-family: Arial, Helvetica, sans-serif !important; }</style>
    <![endif]-->
    <title>${escapeHtml(design.subject)}</title>
  </head>
  <body style="margin:0;padding:0;background-color:${canvasBg} !important;${LIGHT_COLOR_SCHEME_STYLE}">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" bgcolor="${canvasBg}" style="background-color:${canvasBg};margin:0;padding:0;width:100%;">
      <tr>
        <td align="center" bgcolor="${canvasBg}" style="background-color:${canvasBg};padding:28px 24px;">
          <table role="presentation" width="420" cellspacing="0" cellpadding="0" border="0" style="width:100%;max-width:420px;border-collapse:collapse;">
            <tr>
              <td align="center" bgcolor="${c.pageBackground}" style="background-color:${c.pageBackground};padding:22px 24px 8px;font-family:${pageFont};font-size:13px;line-height:1.45;color:${c.congratulationsText};">
                Congratulations <strong>{{employeeName}}</strong><br />on completing <strong>{{bookName}}</strong>! &#127881;
              </td>
            </tr>
            <tr>
              <td${heroBackgroundAttr} bgcolor="${c.pageBackground}" style="${heroBg}padding:28px 20px 32px;">
                ${heroVmlOpen}
                <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
                  <tr>
                    <td align="center" style="font-family:${pageFont};font-size:11px;letter-spacing:0.16em;text-transform:uppercase;color:${c.headlineText};padding-bottom:6px;">
                      ${headline}
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style="font-family:${eventFont};font-size:42px;line-height:1.1;color:${c.eventNameText};padding-bottom:18px;">
                      {{eventName}}
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style="padding:0 18px 18px;">
                      <table role="presentation" width="280" align="center" cellspacing="0" cellpadding="0" border="0" style="width:280px;max-width:280px;">
                        <tr>
                          <td align="center" style="padding:0 0 10px;line-height:0;font-size:0;">
                            <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center" style="margin:0 auto;border:3px solid ${c.bookLogoBorder};border-radius:50%;">
                              <tr>
                                <td style="border-radius:50%;overflow:hidden;line-height:0;font-size:0;">${bookLogo}</td>
                              </tr>
                            </table>
                          </td>
                        </tr>
                        <tr>
                          <td align="center" style="background-color:rgba(255,255,255,0.3);border:1px solid rgba(255,255,255,0.55);border-radius:8px;padding:16px 18px;font-family:${pageFont};font-size:12px;line-height:1.55;color:${c.eventDescriptionText};text-align:center;">
                            {{description}}
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                  <tr>
                    <td align="center" style="font-family:${pageFont};font-size:12px;color:${c.attributionLabelText};padding-bottom:14px;">
                      Organised by : <span style="color:${c.attributionValueText};">{{coordinatorName}}</span>
                    </td>
                  </tr>
                  <tr>
                    <td align="center">${companyLogo}</td>
                  </tr>
                </table>
                ${heroVmlClose}
              </td>
            </tr>
            <tr>
              <td align="center" bgcolor="${c.eventDetailsBackground}" style="background-color:${c.eventDetailsBackground};padding:28px 28px 8px;text-align:center;">
                <p style="margin:0 0 6px;font-family:${pageFont};font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:${c.detailTitleText};">DATE</p>
                <p style="margin:0 0 16px;font-family:${pageFont};font-size:22px;font-weight:bold;color:${c.detailValueText};">{{eventDate}}</p>
                <p style="margin:0 0 6px;font-family:${pageFont};font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:${c.detailTitleText};">TIME</p>
                <p style="margin:0 0 16px;font-family:${pageFont};font-size:22px;font-weight:bold;color:${c.detailValueText};">{{eventTime}}</p>
                <p style="margin:0 0 6px;font-family:${pageFont};font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:${c.detailTitleText};">VENUE</p>
                <p class="quest-venue" x-apple-data-detectors="false" style="margin:0 0 10px;font-family:${pageFont};font-size:13px;line-height:1.5;color:${c.detailValueText};text-decoration:none;">{{venue}}</p>
                <p style="margin:0 0 8px;font-family:${pageFont};font-size:13px;">
                  <a href="{{locationUrl}}" target="_blank" style="color:${c.detailValueText};text-decoration:underline;">Get Directions</a>
                </p>
              </td>
            </tr>
            <tr>
              <td align="center" bgcolor="${c.pageBackground}" style="background-color:${c.pageBackground};padding:28px 24px 32px;text-align:center;">
                <p style="margin:0 0 14px;font-family:${pageFont};font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:${c.detailTitleText};">
                  ${ctaSubject}
                </p>
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" align="center">
                  <tr>
                    <td align="center" bgcolor="${c.ctaBackground}" style="background-color:${c.ctaBackground};border-radius:6px;padding:12px 22px;">
                      <a href="{{takeActionUrl}}" target="_blank" style="font-family:${pageFont};font-size:13px;font-weight:bold;color:${c.ctaText};text-decoration:none;">
                        ${cta}
                      </a>
                    </td>
                  </tr>
                </table>
                <p style="margin:22px 0 0;font-family:${pageFont};font-size:11px;line-height:18px;color:${c.detailTitleText};text-align:center;">
                  Powered by :&nbsp;<img src="${QUEST_LOGO_URL}" alt="The Quest" width="61" height="13" style="display:inline-block;width:61px;height:13px;vertical-align:-2px;border:0;outline:none;" />
                </p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;

  const text = `Congratulations {{employeeName}}
on completing {{bookName}}!

${design.headline}
{{eventName}}

{{description}}

Organised by : {{coordinatorName}}

Date: {{eventDate}}
Time: {{eventTime}}
Venue: {{venue}}
Directions: {{locationUrl}}

${design.ctaSubject}
${design.cta}: {{takeActionUrl}}

Powered by The Quest`;

  return {
    design,
    subject: design.subject,
    html,
    text,
    placeholders: [
      ...new Set([
        'employeeName',
        'bookName',
        'eventName',
        'description',
        'coordinatorName',
        'eventDate',
        'eventTime',
        'venue',
        'locationUrl',
        'takeActionUrl',
        ...[design.subject, design.headline, design.ctaSubject].flatMap(
          (value) =>
            [...String(value || '').matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map(
              (match) => match[1]
            )
        ),
      ]),
    ],
  };
};

export const interpolateInvitePreview = (html, data = {}) =>
  String(html || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
    if (data[key] == null) return '';
    return String(data[key])
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  });
